import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedGoldOrderListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-gold-orders',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, InputTextModule],
  template: `
    <ain-page [title]="t('sales.regulated.gold.title')" [description]="t('sales.regulated.gold.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.regulated.gold.quotesNav')" severity="secondary" (click)="goQuotes()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters" [class.collapsed]="!filtersOpen()">
            <button type="button" class="filter-toggle" (click)="filtersOpen.set(!filtersOpen())">{{ t('sales.regulated.filters.title') }}</button>
            <div class="filter-body filter-grid">
              <label><span>{{ t('sales.regulated.gold.fields.side') }}</span><input pInputText [(ngModel)]="filterSide" (ngModelChange)="applyFilters()" /></label>
              <label><span>{{ t('columns.status') }}</span><input pInputText [(ngModel)]="filterStatus" (ngModelChange)="applyFilters()" /></label>
            </div>
          </div>
          <p-table [value]="filtered()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.gold.fields.side') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('sales.regulated.gold.fields.quantity') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.side }}</button></td>
                <td>{{ row.status }}</td>
                <td>{{ row.quantityGrams }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesGoldOrdersComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly rows = signal<RegulatedGoldOrderListItem[]>([]);
  readonly filtered = signal<RegulatedGoldOrderListItem[]>([]);
  readonly selected = signal<RegulatedGoldOrderListItem[]>([]);
  readonly filtersOpen = signal(true);
  filterSide = '';
  filterStatus = '';
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listGoldOrders().subscribe({
      next: r => { this.rows.set(r); this.applyFilters(); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }
  applyFilters(): void {
    const side = this.filterSide.trim().toUpperCase();
    const st = this.filterStatus.trim().toUpperCase();
    this.filtered.set(this.rows().filter(r =>
      (!side || r.side.toUpperCase().includes(side)) && (!st || r.status.toUpperCase().includes(st))));
  }
  onSelect(v: RegulatedGoldOrderListItem[]): void { this.selected.set(v ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/gold-orders/new']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/gold-orders', id]); }
  goQuotes(): void { void this.router.navigate(['/sales/gold-quotes']); }
  t(key: string): string { return this.localization.translate(key); }
}
